import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useDemo } from "@/contexts/DemoContext";
import { useDemoStoreOptional } from "@/contexts/DemoStoreContext";
import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/utils";
import type { Branch } from "@/types/branch";

type BranchMutation = Partial<Branch> & { id?: string };

export function useBranches() {
  const { isDemo } = useDemo();
  const store = useDemoStoreOptional();
  const { profile } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["branches", profile?.business_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("branches")
        .select("*")
        .eq("business_id", profile!.business_id)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data ?? []) as Branch[];
    },
    enabled: !isDemo && !!profile?.business_id,
  });

  const branches: Branch[] = isDemo
    ? ((store?.branches ?? []) as Branch[])
    : (query.data ?? []);

  const upsertBranch = useMutation({
    mutationFn: async (branch: BranchMutation) => {
      if (isDemo && store) {
        return store.upsertBranch(branch);
      }

      const { id, ...rest } = branch;
      const payload: TablesUpdate<"branches"> = {
        ...rest,
        business_id: profile?.business_id ?? branch.business_id,
      };

      if (id) {
        const { data, error } = await supabase
          .from("branches")
          .update(payload)
          .eq("id", id)
          .select()
          .single();

        if (error) throw error;
        return data as Branch;
      }

      if (!payload.business_id || !payload.name) {
        throw new Error("Branch name and business are required.");
      }

      const insertPayload: TablesInsert<"branches"> = {
        ...payload,
        business_id: payload.business_id,
        name: payload.name,
      };

      const { data, error } = await supabase
        .from("branches")
        .insert(insertPayload)
        .select()
        .single();

      if (error) throw error;
      return data as Branch;
    },
    onSuccess: () => {
      if (!isDemo) {
        qc.invalidateQueries({ queryKey: ["branches"] });
      }
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const deleteBranch = useMutation({
    mutationFn: async (id: string) => {
      if (isDemo && store) {
        store.deleteBranch(id);
        return;
      }

      const { error } = await supabase.from("branches").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      if (!isDemo) {
        qc.invalidateQueries({ queryKey: ["branches"] });
      }
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  return {
    branches,
    isLoading: !isDemo && query.isLoading,
    upsertBranch,
    deleteBranch,
  };
}
